import type { PaletteKey, ThemeKey } from '../types';

import { requestMutation } from '../lib/fasterdom/fasterdom';
import classicColors from '../styles/themes/classic.json';
import runColors from '../styles/themes/run.json';
import { animate } from './animation';
import { hex2rgbaObj, lerpRgbaObj } from './colors.ts';

let isInitialized = false;
let currentPalette: PaletteKey = 'run';

const DECIMAL_PLACES = 3;
const DURATION_MS = 200;
const ENABLE_ANIMATION_DELAY_MS = 500;
const RGB_VARIABLES = new Set([
  '--color-text',
  '--color-primary-shade',
  '--color-text-secondary',
  '--color-accent-own',
]);

const PALETTES: Record<PaletteKey, Record<string, [string, string]>> = {
  run: runColors as unknown as Record<string, [string, string]>,
  classic: classicColors as unknown as Record<string, [string, string]>,
};

const THEME_META_COLORS: Record<PaletteKey, { light: string; dark: string }> = {
  run: { light: '#F9FDFF', dark: '#0A0B14' },
  classic: { light: '#fff', dark: '#212121' },
};

const DISABLE_ANIMATION_CSS = `
.no-animations #root *,
.no-animations #root *::before,
.no-animations #root *::after {
  transition: none !important;
}`;

function buildColors(palette: PaletteKey) {
  const paletteColors = PALETTES[palette];
  return (Object.keys(paletteColors) as string[]).map((property) => ({
    property,
    colors: [hex2rgbaObj(paletteColors[property][0]), hex2rgbaObj(paletteColors[property][1])],
  }));
}

let colors = buildColors('run');

const injectCss = (css: string) => {
  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);
  return () => {
    document.head.removeChild(style);
  };
};

const switchTheme = (theme: ThemeKey, withAnimation: boolean, _palette?: PaletteKey) => {
  const newPalette: PaletteKey = 'run'; // Force Run palette, classic is disabled
  const paletteChanged = newPalette !== currentPalette;

  if (paletteChanged) {
    currentPalette = newPalette;
    colors = buildColors(newPalette);
  }

  const themeClassName = `theme-${theme}`;
  const isDarkTheme = theme === 'dark';
  const shouldAnimate = isInitialized && withAnimation;
  const startIndex = isDarkTheme ? 0 : 1;
  const endIndex = isDarkTheme ? 1 : 0;
  const startAt = Date.now();
  const themeColorTag = document.querySelector('meta[name="theme-color"]');

  const themeAlreadySet = document.documentElement.classList.contains(themeClassName);
  if (themeAlreadySet && !paletteChanged) {
    return;
  }

  requestMutation(() => {
    if (!themeAlreadySet) {
      document.documentElement.classList.remove(`theme-${isDarkTheme ? 'light' : 'dark'}`);
    }
    let uninjectCss: (() => void) | undefined;
    if (isInitialized && !paletteChanged) {
      uninjectCss = injectCss(DISABLE_ANIMATION_CSS);
      document.documentElement.classList.add('no-animations');
    }
    if (!themeAlreadySet) {
      document.documentElement.classList.add(themeClassName);
    }

    // Palette class for CSS-level palette-specific rules (wallpapers, effects)
    document.documentElement.classList.remove('palette-run', 'palette-classic');
    document.documentElement.classList.add(`palette-${newPalette}`);

    // Layer B effects: only enabled for Run palette
    if (newPalette === 'run') {
      document.documentElement.classList.add('theme-effects-enabled');
    } else {
      document.documentElement.classList.remove('theme-effects-enabled');
    }

    if (themeColorTag) {
      const metaColors = THEME_META_COLORS[newPalette];
      themeColorTag.setAttribute('content', isDarkTheme ? metaColors.dark : metaColors.light);
    }

    setTimeout(() => {
      requestMutation(() => {
        uninjectCss?.();
        document.documentElement.classList.remove('no-animations');
      });
    }, ENABLE_ANIMATION_DELAY_MS);

    isInitialized = true;

    if (shouldAnimate && !paletteChanged) {
      animate(() => {
        const t = Math.min((Date.now() - startAt) / DURATION_MS, 1);
        applyColorAnimationStep(startIndex, endIndex, transition(t));
        return t < 1;
      }, requestMutation);
    } else {
      applyColorAnimationStep(startIndex, endIndex);
    }
  });
};

function transition(t: number) {
  return 1 - ((1 - t) ** 3.5);
}

function applyColorAnimationStep(startIndex: number, endIndex: number, interpolationRatio: number = 1) {
  colors.forEach(({ property, colors: propertyColors }) => {
    const {
      r, g, b, a,
    } = lerpRgbaObj(propertyColors[startIndex], propertyColors[endIndex], interpolationRatio);

    const roundedA = a !== undefined ? Math.round((a / 255) * 10 ** DECIMAL_PLACES) / 10 ** DECIMAL_PLACES : undefined;

    document.documentElement.style.setProperty(property, `rgb(${r},${g},${b}${roundedA ? `,${roundedA}` : ''})`);

    if (RGB_VARIABLES.has(property)) {
      document.documentElement.style.setProperty(`${property}-rgb`, `${r},${g},${b}`);
    }
  });
}

export default switchTheme;
